import { ActionIdentifier } from '../enums/ActionIdentifier'
import { Charge } from './Charge.interface'
import { TypePeriod } from './Plan.interface'
import { Promotion } from './Promotion.interface'
import { UserPgc } from './User.interface'

/* GET subscriber */
export interface SubscriberResponse {
	content: Subscriber[]
	number: number
	numberOfElements: number
	totalPages: number
	totalElements: number
}

export interface Subscriber {
	active: boolean
	status: number
	statusUpdateDate: string
	id: number
	email: string
	oauthIdentifier: string | null
	validated: boolean
	validateDate: string | null
	isEnterprise: boolean
	name: string | null
	surname: string | null
	corporateName: string | null
	fiscalCode: string | null
	phone: string | null
	postalCode: string | null
	createdAtDate: string
	expiredDate: string | null
	srConsumerId: string | null
	idLibLanguage: number
	spam: boolean
	allowCommercialEmails: boolean
	code: string
	blockedEmail: boolean
	isBar: boolean
	addressAnnotations: string | null
	googleAddress?: SubscriberGoogleAddress | null
	addressAnnotationsBilling: string | null
	googleAddressBilling?: SubscriberGoogleAddress | null
	libCountry: SubscriberLibCountry | null
	libCountryPhone?: LibCountryPhone | null
	subscriberPlans?: SubscriberPlan[]
	paidAmount: number
	paidPayments: number
	pendingAmount: number
	pendingPayments: number
	fullname: string
	actionHistory: ActionHistory[]
	tokenExpiresAt: string
}

export interface Rebound {
	active: boolean
	createDate: string
	id: string
	bounceDate: string
	emailSubject: string
	emailSubscriber: string
	bounceReason: string
}

export interface ReboundResponse {
	content: Rebound[]
	number: number
	numberOfElements: number
	totalPages: number
	totalElements: number
}

export interface ActionHistory {
	active: boolean
	createUser: string
	id: number
	dateAction: string
	actionIdentifier: ActionIdentifier
	actionDescription: string | null
	reason: string | null
}

export interface SubscriberFilterSearch {
	id: number
	fullname: string
}

export interface SubscriberGoogleAddress {
	active?: boolean
	id?: number
	address_components: SubscriberGoogleAddressComponent[]
	formatted_address: string
	place_id: string
}

export interface SubscriberGoogleAddressComponent {
	longName: string
	shortName: string
	types: string[]
}

export interface SubscriberLibCountry {
	id: number
	isoCode: string
	translations: SubscriberCountryTranslation[]
}

export interface SubscriberCountryTranslation {
	name: string
}

export interface SubscriberPlan {
	id: number
	active?: boolean
	dateStart: string
	dateEnd: string | null
	cancelSubscriptionDate: Date | null
	unsubscribeDate: Date | null
	price: number
	plan: SubscriberPlanDetails
	subscriberPayMethod: SubscriberPayMethod | null
	offerSubscriberPlans: SubscriberOffer[]
	payments?: SubscriberPayment[]
	status: number
	extensionPlans: ExtendedPlan[]
}

export interface SubscriberPlanDetails {
	id: number
	price?: number
	translations: SubscriberPlanTranslation[]
	libSubscriptionType: SubscriberLibSubscriptionType
	libPaymentType: SubscriberLibPaymentType
}

export interface SubscriberPayMethod {
	id: number
	libPayMethod: SubscriberLibPayMethod
	reference: string
	validationDate: string
}

export interface SubscriberOffer {
	id: number
	active?: boolean
	offer: Promotion
	dateStart?: Date
	dateEnd?: Date
}

export interface SubscriberPlanTranslation {
	name: string
}

export interface SubscriberLibSubscriptionType {
	detail: boolean
	id: number
	description: string
}

export interface SubscriberLibPaymentType {
	id: number
	description: string
}

export interface SubscriberLibPayMethod {
	srReference: string
}

export interface SubscriberOfferTranslation {
	name: string
}

export interface SubscriberBill {
	id: number
	code: string
}

export interface SubscriberPayment {
	id: number
	bill: SubscriberBill | null
	billReturned: SubscriberBill | null
	import: number
	status: number
	active?: boolean
	dateExecution?: string
}

/* POST subscriber */
export interface CreateSubscriber {
	email: string
	isEnterprise: boolean
	name: string
	surname: string
	fiscalCode?: string
	libCountry: LIBCountry
	libCountryPhone: LibCountryPhone
	phone: string
	postalCode: string
	googleAddressBilling?: SubscriberGoogleAddress
	addressAnnotationsBilling?: string
	corporateName?: string
}

export interface LIBCountry {
	id: number
}

/* POST create subscriber plan */
export interface SubscriberPlanPayment {
	planId: number
	paymentToken?: string
	typeUser: string
	discountCode?: string
	paymentConfirm?: boolean
	paymentId?: string
}

export interface PaymentResponse {
	clientSecret?: string
	payment?: Charge | null
	status?: string | number
	isPostPaid?: boolean
}

/* PUT subscriber */
export interface UpdateSubscriber {
	id: number
	active?: boolean
	status?: number
	statusUpdateDate?: string
	email?: string
	newEmail?: string
	oldEmail?: string
	oauthIdentifier?: string
	validated?: boolean
	validateDate?: string
	isEnterprise?: boolean
	name?: string
	surname?: string
	corporateName?: string | null
	fiscalCode?: string | null
	phone?: string
	libCountryPhone?: LibCountryPhone | null
	postalCode?: string
	createdAtDate?: string
	expiredDate?: string
	srConsumerId?: string
	spam?: boolean
	allowCommercialEmails?: boolean
	code?: string
	addressAnnotations?: string
	googleAddress?: SubscriberGoogleAddress
	addressAnnotationsBilling?: string | null
	googleAddressBilling?: SubscriberGoogleAddress
	libCountry?: SubscriberLibCountry
	subscriberPlans?: SubscriberPlan[]
}
export interface LibCountryPhone {
	id?: number
	isoCode?: string
	phonePrefix?: string
}

export interface ExtendedPlan {
	id: number
	active?: boolean
	amount?: number
	period?: TypePeriod
	reason?: string
	dateExtension?: Date
	dateStart?: Date
	dateEnd?: Date
	userPgc?: UserPgc
	subscriberPlan?: SubscriberPlan
}

/* PUT subscriber plan */
export interface UpdateSubscriberPlan {
	id: number
	planId: number
	discountCode?: string
	cancelOffer?: boolean
	offerId?: number
}

export interface ValidationCode {
	discount: boolean
	offer: Promotion[]
}

export interface CancellationOfferSummary {
	id: number
	name?: string
	amount: number
	discountType: number
	cancellationOffer: boolean
}

export interface CancellationOfferPlan {
	id: number
	name?: string
}

export interface CancellationOfferPricing {
	baseAmount: number
	discountAmount: number
	finalAmount: number
	currency: string
}

export interface CancellationOfferEligibility {
	discount: boolean
	offer?: CancellationOfferSummary | null
	selectedOffer?: CancellationOfferSummary | null
	eligibleOffers?: CancellationOfferSummary[]
	planId: number | null
	plan?: CancellationOfferPlan | null
	pricing?: CancellationOfferPricing | null
}

export enum StatusSubscriberPlan {
	ACTIVE = 0,
	PENDING = 1,
	CANCELLED = 2,
	COMPLETED = 3,
	NO_RENEWABLE = 4
}

export interface CheckChangeEmailResponse {
	existEmail: boolean
}
